// Vendor
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Components
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { Dropdown } from 'src/components/molecules/Dropdown';
import Modal, { ModalVariant, ModalWidth } from 'src/components/organisms/Modal';

// Redux
import { useSelector } from 'react-redux';
import {
  selectors as disputeSelectors,
  getDisputeCategories,
  resetSendDispute,
  sendDispute
} from 'src/features/disputes';
import { DisputeCategory } from 'src/features/disputes/api';
import { useAppDispatch } from 'src/store';
import { Status as StatusTypes } from 'src/ts/enums';
import { Container, FieldContainer, Footer } from './styles';

// Hooks
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';

export interface DisputeModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  entryId: string;
  applicantName?: string;
}

interface FormData {
  dispute_category_id: string;
  description: string;
}

const DisputeModal: FC<DisputeModalProps> = ({
  showModal,
  setShowModal,
  entryId,
  applicantName
}) => {
  const dispatch = useAppDispatch();
  const { showSnackbar, SnackTypes, VariantType } = useSnackbarProvider();
  const methods = useForm<FormData>({
    defaultValues: {
      dispute_category_id: '',
      description: ''
    }
  });

  const categories = useSelector(disputeSelectors.categories);
  const categoriesStatus = useSelector(disputeSelectors.categoriesStatus);
  const sendDisputeStatus = useSelector(disputeSelectors.sendDisputeStatus);
  const sendDisputeError = useSelector(disputeSelectors.sendDisputeError);

  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (showModal && categoriesStatus === StatusTypes.IDLE) {
      dispatch(getDisputeCategories());
    }
  }, [showModal, categoriesStatus, dispatch]);

  useEffect(() => {
    if (sendDisputeStatus === StatusTypes.SUCCESS) {
      showSnackbar(
        VariantType.success,
        'Your dispute has been submitted to our Fraud Operations team. You will receive an email confirmation shortly.',
        SnackTypes.none
      );
      methods.reset();
      setLocalError(null);
      setShowModal(false);
      dispatch(resetSendDispute());
    }
  }, [sendDisputeStatus, methods, setShowModal, dispatch, showSnackbar, VariantType, SnackTypes]);

  const onCancel = () => {
    methods.reset();
    setLocalError(null);
    setShowModal(false);
  };

  const onSubmit = (data: FormData) => {
    setLocalError(null);
    dispatch(
      sendDispute({
        entry_id: entryId,
        dispute_category_id: data.dispute_category_id,
        description: data.description?.trim() || ''
      })
    );
  };

  const selectedCategoryId = methods.watch('dispute_category_id');
  const description = methods.watch('description');

  const categoryOptions = (categories || []).map((category: DisputeCategory) => ({
    value: category.id,
    label: category.name,
    isSelected: category.id === selectedCategoryId,
    isPlaceHolder: false
  }));

  const isLoading =
    categoriesStatus === StatusTypes.LOADING || sendDisputeStatus === StatusTypes.LOADING;

  const isFormValid = selectedCategoryId && description?.trim();

  return (
    <Modal
      className="DisputeModal"
      variant={ModalVariant.none}
      title="Log Dispute"
      showModal={showModal}
      setShowModal={setShowModal}
      onCancel={onCancel}
      isBackClosable={false}
      isEscapeClosable={true}
      showCloseButton
      showModalActions={false}
      width={ModalWidth.large}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Container>
            <Text variant={TextVariant.normal} color={TextColor.gray600}>
              Fill out the form below to open a dispute. Our team will carefully review your
              submission.
            </Text>

            {applicantName && (
              <FieldContainer>
                <Text variant={TextVariant.small} color={TextColor.gray700} isBold>
                  Applicant:
                </Text>
                <Text variant={TextVariant.normal} color={TextColor.gray700}>
                  {applicantName}
                </Text>
              </FieldContainer>
            )}

            <FieldContainer>
              <Dropdown
                name="dispute_category_id"
                data={categoryOptions}
                placeholder="Select dispute category"
                isDisabled={isLoading}
                showLabel={false}
                valueInitial={selectedCategoryId}
              />
            </FieldContainer>

            <FieldContainer style={{ flex: 1, minHeight: '150px' }}>
              <InputText
                name="description"
                type={InputTextType.text}
                placeholder="Type a description of the dispute here..."
                isDisabled={isLoading}
                multiline={true}
                rows={8}
                showOptionalLabel={false}
                showLabel={false}
              />
            </FieldContainer>

            {(localError || sendDisputeError) && (
              <Text variant={TextVariant.normal} color={TextColor.error}>
                {localError || sendDisputeError}
              </Text>
            )}

            <Footer>
              <Button
                name="button-cancel"
                onClick={onCancel}
                size={ButtonSize.normal}
                variant={ButtonVariant.outline}
                color={ButtonColor.primary}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                name="button-submit"
                type={ButtonType.submit}
                size={ButtonSize.normal}
                color={ButtonColor.primary}
                variant={ButtonVariant.contained}
                isLoading={isLoading}
                isDisabled={isLoading || !isFormValid}
              >
                Submit
              </Button>
            </Footer>
          </Container>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default DisputeModal;
